import React, { useEffect, useState } from 'react';

import { DebugToggle } from 'components/Debug/DebugToggle';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Switch } from 'components/ui/switch';
import { useToast } from 'components/ui/use-toast';
import {
  adminContent,
  type AnalyticsOperationsStatus,
  type MeetingScheduledEmailsSetting,
} from 'services/api/adminContentClient';
import AppSettingsSection from './AppSettingsSection';
import RoleplayPerformanceCard from './RoleplayPerformanceCard';

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'Not yet available';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatJobName = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const Operations: React.FC = () => {
  const { addToast } = useToast();
  const [isRunningNightlyJobs, setIsRunningNightlyJobs] = useState(false);
  const [meetingScheduledEmailsSetting, setMeetingScheduledEmailsSetting] =
    useState<MeetingScheduledEmailsSetting | null>(null);
  const [isLoadingMeetingScheduledEmails, setIsLoadingMeetingScheduledEmails] = useState(true);
  const [isSavingMeetingScheduledEmails, setIsSavingMeetingScheduledEmails] = useState(false);
  const [analyticsStatus, setAnalyticsStatus] = useState<AnalyticsOperationsStatus | null>(null);
  const [isLoadingAnalyticsStatus, setIsLoadingAnalyticsStatus] = useState(true);
  const [analyticsActionInFlight, setAnalyticsActionInFlight] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    adminContent.jobs.getAnalyticsStatus()
      .then((response) => {
        if (isMounted) {
          setAnalyticsStatus(response.data);
        }
      })
      .catch(() => {
        if (isMounted) {
          addToast({
            title: 'Failed to load analytics rollup status',
            description: 'Please refresh and try again.',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingAnalyticsStatus(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  useEffect(() => {
    let isMounted = true;
    adminContent.operations.getMeetingScheduledEmails()
      .then((response) => {
        if (isMounted) {
          setMeetingScheduledEmailsSetting(response.data);
        }
      })
      .catch(() => {
        if (isMounted) {
          addToast({
            title: 'Failed to load meeting email setting',
            description: 'Please refresh and try again.',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingMeetingScheduledEmails(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  const handleRunNightlyJobs = async () => {
    setIsRunningNightlyJobs(true);
    try {
      await adminContent.jobs.runNightly();
      addToast({
        title: 'Nightly jobs queued',
        description: 'The nightly safety checks are now running in the background.',
      });
    } catch {
      addToast({
        title: 'Failed to trigger nightly jobs',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsRunningNightlyJobs(false);
    }
  };

  const handleUpdateMeetingScheduledEmails = async (enabled: boolean) => {
    setIsSavingMeetingScheduledEmails(true);
    try {
      const response = await adminContent.operations.updateMeetingScheduledEmails(enabled);
      setMeetingScheduledEmailsSetting(response.data);
      addToast({
        title: 'Meeting scheduled emails updated',
        description: enabled
          ? 'Invitees will receive an email when new meetings are scheduled.'
          : 'Newly scheduled meetings will not send invitee emails.',
      });
    } catch {
      addToast({
        title: 'Failed to update meeting email setting',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingMeetingScheduledEmails(false);
    }
  };

  const refreshAnalyticsStatus = async () => {
    const response = await adminContent.jobs.getAnalyticsStatus();
    setAnalyticsStatus(response.data);
  };

  const handleRunAnalyticsAction = async ({
    actionKey,
    confirmMessage,
    successTitle,
    successDescription,
    errorTitle,
    request,
  }: {
    actionKey: string;
    confirmMessage: string;
    successTitle: string;
    successDescription: string;
    errorTitle: string;
    request: () => Promise<unknown>;
  }) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setAnalyticsActionInFlight(actionKey);
    try {
      await request();
      addToast({
        title: successTitle,
        description: successDescription,
      });
      await refreshAnalyticsStatus();
    } catch {
      addToast({
        title: errorTitle,
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setAnalyticsActionInFlight(null);
    }
  };

  return (
    <section className="space-y-8 px-6 py-8">
      <div className="max-w-3xl space-y-2">
        <h1 className="text-3xl font-semibold text-foreground">Operations</h1>
        <p className="text-sm text-muted-foreground">
          Internal admin actions live here instead of sitting beside full workspaces in the header.
        </p>
      </div>

      <AppSettingsSection />

      <Card className="w-full max-w-none" maxWidth="none">
        <CardHeader>
          <CardTitle>Nightly Jobs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 items-stretch">
          <p className="text-sm text-muted-foreground">
            Queue the nightly background jobs for safety checks and maintenance tasks.
          </p>
          <Button size="auto" onClick={handleRunNightlyJobs} disabled={isRunningNightlyJobs}>
            {isRunningNightlyJobs ? 'Queuing...' : 'Run Nightly Jobs'}
          </Button>
        </CardContent>
      </Card>

      <Card className="w-full max-w-none" maxWidth="none">
        <CardHeader>
          <CardTitle>Analytics Rollups</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 items-stretch">
          <p className="text-sm text-muted-foreground">
            Monitor analytics rollup freshness and queue manual rebuild or repair jobs when reporting data needs to be refreshed.
          </p>
          {isLoadingAnalyticsStatus ? (
            <p className="text-sm text-muted-foreground">Loading analytics rollup status...</p>
          ) : analyticsStatus ? (
            <>
              <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Backfill complete</span>
                  <strong className="text-foreground">{analyticsStatus.backfill_complete ? 'Yes' : 'No'}</strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last successful refresh</span>
                  <strong className="text-foreground">{formatDateTime(analyticsStatus.last_successful_run_at)}</strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rollup version</span>
                  <strong className="text-foreground">{analyticsStatus.rollup_version}</strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cache namespace</span>
                  <strong className="text-foreground">{analyticsStatus.cache_namespace_version}</strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last processed event</span>
                  <strong className="text-foreground">
                    {analyticsStatus.last_processed_event_id
                      ? `${analyticsStatus.last_processed_event_id} at ${formatDateTime(analyticsStatus.last_processed_event_at)}`
                      : 'Not yet available'}
                  </strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last processed bug report</span>
                  <strong className="text-foreground">
                    {analyticsStatus.last_processed_bug_report_id
                      ? `${analyticsStatus.last_processed_bug_report_id} at ${formatDateTime(analyticsStatus.last_processed_bug_report_at)}`
                      : 'Not yet available'}
                  </strong>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last processed user join</span>
                  <strong className="text-foreground">
                    {analyticsStatus.last_processed_user_id
                      ? `${analyticsStatus.last_processed_user_id} at ${formatDateTime(analyticsStatus.last_processed_user_joined_at)}`
                      : 'Not yet available'}
                  </strong>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="auto"
                  onClick={() => void handleRunAnalyticsAction({
                    actionKey: 'backfill',
                    confirmMessage: 'Reprocess all analytics data? This rebuilds all analytics rollups from the full source history.',
                    successTitle: 'Analytics backfill queued',
                    successDescription: 'The full analytics rebuild is now running in the background.',
                    errorTitle: 'Failed to queue analytics backfill',
                    request: () => adminContent.jobs.runAnalyticsBackfill(),
                  })}
                  disabled={analyticsActionInFlight !== null}
                >
                  {analyticsActionInFlight === 'backfill' ? 'Queuing...' : 'Reprocess All Analytics Data'}
                </Button>
                <Button
                  size="auto"
                  variant="secondary"
                  onClick={() => void handleRunAnalyticsAction({
                    actionKey: 'reconciliation',
                    confirmMessage: 'Reprocess recent analytics data? This rebuilds the recent analytics window from source data.',
                    successTitle: 'Analytics reconciliation queued',
                    successDescription: 'The analytics reconciliation job is now running in the background.',
                    errorTitle: 'Failed to queue analytics reconciliation',
                    request: () => adminContent.jobs.runAnalyticsReconciliation(),
                  })}
                  disabled={analyticsActionInFlight !== null}
                >
                  {analyticsActionInFlight === 'reconciliation' ? 'Queuing...' : 'Reprocess Recent Analytics Data'}
                </Button>
                <Button
                  size="auto"
                  variant="secondary"
                  onClick={() => void handleRunAnalyticsAction({
                    actionKey: 'user-summary-refresh',
                    confirmMessage: 'Refresh analytics user summaries? This recalculates the Users tab summary layer.',
                    successTitle: 'Analytics user summary refresh queued',
                    successDescription: 'The user summary refresh is now running in the background.',
                    errorTitle: 'Failed to queue analytics user summary refresh',
                    request: () => adminContent.jobs.runAnalyticsUserSummaryRefresh(),
                  })}
                  disabled={analyticsActionInFlight !== null}
                >
                  {analyticsActionInFlight === 'user-summary-refresh' ? 'Queuing...' : 'Refresh User Summaries'}
                </Button>
                <Button
                  size="auto"
                  variant="ghost"
                  onClick={() => void refreshAnalyticsStatus()}
                  disabled={analyticsActionInFlight !== null}
                >
                  Refresh Status
                </Button>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Recent analytics jobs</h3>
                {analyticsStatus.recent_jobs.length ? (
                  <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
                    {analyticsStatus.recent_jobs.map((job) => (
                      <div
                        key={`${job.job_name}-${job.started_at ?? 'pending'}`}
                        className="rounded-lg border border-border/60 bg-background px-4 py-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-foreground">{formatJobName(job.job_name)}</p>
                            <p className="text-xs text-muted-foreground">
                              Started {formatDateTime(job.started_at)}
                            </p>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            <p>Status: <span className="font-medium text-foreground">{job.status}</span></p>
                            <p>Duration: {job.duration_ms ? `${job.duration_ms.toLocaleString()} ms` : 'In progress'}</p>
                          </div>
                        </div>
                        {Object.keys(job.summary).length ? (
                          <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
                            {Object.entries(job.summary).map(([key, value]) => (
                              <div key={key}>
                                <span className="block uppercase tracking-wide">{formatJobName(key)}</span>
                                <strong className="text-foreground">{String(value ?? '')}</strong>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {job.errors.length ? (
                          <div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                            {job.errors.join(' ')}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No analytics job history is available yet.</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Analytics rollup status is unavailable.</p>
          )}
        </CardContent>
      </Card>

      <RoleplayPerformanceCard />

      <Card className="w-full max-w-none" maxWidth="none">
        <CardHeader>
          <CardTitle>Developer Tools</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 items-stretch">
          <p className="text-sm text-muted-foreground">
            Enable debug mode here instead of keeping developer controls in the global header.
          </p>
          <div className="flex items-center justify-start">
            <DebugToggle className="h-9 rounded-full px-4 text-sm font-semibold" />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full max-w-none" maxWidth="none">
        <CardHeader>
          <CardTitle>Meeting Scheduled Emails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 items-stretch">
          <p className="text-sm text-muted-foreground">
            Control whether invitees receive an email as soon as a new meeting is scheduled.
          </p>
          {isLoadingMeetingScheduledEmails ? (
            <p className="text-sm text-muted-foreground">Loading meeting email setting...</p>
          ) : meetingScheduledEmailsSetting ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Switch
                  id="meeting-scheduled-emails-enabled"
                  aria-label="Meeting scheduled emails"
                  checked={meetingScheduledEmailsSetting.enabled}
                  disabled={isSavingMeetingScheduledEmails}
                  onCheckedChange={(checked) => { void handleUpdateMeetingScheduledEmails(checked); }}
                />
                <label
                  htmlFor="meeting-scheduled-emails-enabled"
                  className="text-sm font-medium text-foreground"
                >
                  {meetingScheduledEmailsSetting.enabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
              {isSavingMeetingScheduledEmails ? (
                <p className="text-sm text-muted-foreground">Saving meeting email setting...</p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Meeting email setting is unavailable.</p>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default Operations;
